import type { CaseSummary } from './types';
import { isLegacyCase } from './types';

export interface LegacyCaseMeta {
  title: string;
  shortLocation: string;
  year: number;
  teaser: string;
}

export const LEGACY_METADATA: Record<string, LegacyCaseMeta> = {
  'TU-1980-000001': {
    title: 'The Rendlesham Forest Incident',
    shortLocation: 'Suffolk, UK',
    year: 1980,
    teaser: 'US airmen near RAF Woodbridge reported lights and a craft in the forest over several nights; the deputy base commander’s memo describes ground marks and raised radiation readings.',
  },
  'TU-1947-000001': {
    title: 'The Roswell Incident',
    shortLocation: 'Lincoln County, New Mexico',
    year: 1947,
    teaser: 'A rancher found debris; the Army announced it had a “flying disc”, then said it was a weather balloon. A 1994 US Air Force report linked it to the secret Project Mogul.',
  },
  'TU-1997-000001': {
    title: 'The Phoenix Lights',
    shortLocation: 'Phoenix & Prescott, Arizona',
    year: 1997,
    teaser: 'Lights were reported across Arizona: a V-shaped formation, then a row of hovering lights over Phoenix. Both have been attributed to A-10 aircraft on a training programme and the flares they dropped.',
  },
  'TU-1990-000001': {
    title: 'The Calvine Incident',
    shortLocation: 'Perthshire, Scottish Highlands',
    year: 1990,
    teaser: 'Two people photographed a large diamond-shaped object near a military jet. The MoD kept a print for decades; researcher David Clarke published it in 2022.',
  },
  'TU-1966-000001': {
    title: 'Westall UFO Incident',
    shortLocation: 'Melbourne, Victoria, Australia',
    year: 1966,
    teaser: 'Students and staff at Westall High School, Melbourne, said they watched a silver saucer-shaped object descend near the school in broad daylight.',
  },
  'TU-1994-000001': {
    title: 'Ariel School Phenomenon',
    shortLocation: 'Ruwa, Zimbabwe',
    year: 1994,
    teaser: 'Pupils at a Zimbabwean primary school described a craft landing beyond the playground at break time; many drew strikingly similar pictures.',
  },
  'TU-1967-000001': {
    title: 'The Shag Harbour Incident',
    shortLocation: 'Shelburne County, Nova Scotia',
    year: 1967,
    teaser: 'At least eleven people saw a lit object fall into the sea off Nova Scotia, and RCMP officers watched it sink. Navy divers searched for three days and found nothing.',
  },
  'TU-1977-000001': {
    title: 'Broad Haven Triangle',
    shortLocation: 'Pembrokeshire, Wales',
    year: 1977,
    teaser: 'Fourteen pupils at a Welsh primary school said a yellow cigar-shaped craft landed in a field beside the school. An MoD investigator found no evidence of a landing.',
  },
  'TU-2006-000001': {
    title: "Chicago O'Hare Gate C17",
    shortLocation: "O'Hare Airport, Chicago, Illinois",
    year: 2006,
    teaser: 'United Airlines staff reported a dark, disc-like object hovering over gate C17 before shooting up through the clouds. The FAA attributed it to a weather phenomenon.',
  },
  'TU-2008-000001': {
    title: 'Stephenville Radar Event',
    shortLocation: 'Erath County, Texas',
    year: 2008,
    teaser: 'Dozens of residents reported patterns of lights in the night sky. The Air Force Reserve said F-16s had been training nearby and dropping flares.',
  },
};

function capitalizeWords(str: string): string {
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getCaseTitle(item: {
  historic_title?: string;
  case_number?: string | null;
  shape?: string | null;
  colour?: string | null;
  location_name?: string | null;
}): string {
  if (item.historic_title) return item.historic_title;
  if (item.case_number) {
    const normalized = item.case_number.replace(/^SS-/, 'TU-').toUpperCase();
    if (LEGACY_METADATA[normalized]) {
      return LEGACY_METADATA[normalized].title;
    }
  }

  const rawColour = (item.colour || '').trim();
  const rawShape = (item.shape || '').trim();

  // Clean composite strings like "matte dark charcoal diamond / rhomboid"
  const cleanColour = rawColour
    ? rawColour.split('/')[0].replace(/diamond|rhomboid|disc|sphere|triangle|cigar/gi, '').trim()
    : '';

  const shapeLabel = rawShape && rawShape !== 'unknown' && rawShape !== 'other'
    ? rawShape.replaceAll('_', ' ')
    : 'craft';

  if (cleanColour) {
    return capitalizeWords(`${cleanColour} ${shapeLabel}`);
  }
  if (rawShape && rawShape !== 'unknown') {
    return `${capitalizeWords(shapeLabel)} Observation`;
  }
  if (item.location_name) {
    const loc = item.location_name.split(',')[0].trim();
    return `Observation in ${loc}`;
  }
  return 'Unidentified Aerial Observation';
}

export function getCaseTeaser(item: {
  historic_teaser?: string;
  case_number?: string | null;
  description?: string | null;
  duration_seconds?: number | null;
  witness_count?: number | null;
}): string {
  if (item.historic_teaser) return item.historic_teaser;
  if (item.case_number) {
    const normalized = item.case_number.replace(/^SS-/, 'TU-').toUpperCase();
    if (LEGACY_METADATA[normalized]) {
      return LEGACY_METADATA[normalized].teaser;
    }
  }

  if (item.description) {
    const clean = item.description.replace(/\s+/g, ' ').trim();
    const firstSentence = clean.split(/[.!?]/)[0].trim();
    if (firstSentence.length > 25 && firstSentence.length < 130) {
      return `${firstSentence}.`;
    }
    return clean.slice(0, 110).trim() + '…';
  }

  const parts: string[] = [];
  if (item.witness_count && item.witness_count > 1) {
    parts.push(`${item.witness_count} witnesses recorded`);
  }
  if (item.duration_seconds) {
    const min = Math.round(item.duration_seconds / 60);
    parts.push(`Duration ~${min > 0 ? `${min}m` : `${item.duration_seconds}s`}`);
  }
  return parts.length ? parts.join(' · ') : 'Public witness record logged in Track the Unknown archive.';
}

/** Public references for the historic dossiers, shown under each archive summary. */
export const LEGACY_SOURCES: Record<string, Array<{ label: string; url: string }>> = {
  'TU-1980-000001': [{ label: 'Rendlesham Forest incident (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Rendlesham_Forest_incident' }],
  'TU-1947-000001': [{ label: 'Roswell incident (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Roswell_incident' }],
  'TU-1997-000001': [{ label: 'Phoenix Lights (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Phoenix_Lights' }],
  'TU-1990-000001': [{ label: 'Calvine UFO photograph (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Calvine_UFO_photograph' }],
  'TU-1966-000001': [{ label: 'Westall UFO (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Westall_UFO' }],
  'TU-1994-000001': [{ label: 'Ariel School UFO incident (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Ariel_School_UFO_incident' }],
  'TU-1967-000001': [{ label: 'Shag Harbour UFO incident (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Shag_Harbour_UFO_incident' }],
  'TU-1977-000001': [{ label: 'Broad Haven Triangle (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Broad_Haven_Triangle' }],
  'TU-2006-000001': [{ label: '2006 O’Hare International Airport UFO sighting (Wikipedia)', url: "https://en.wikipedia.org/wiki/2006_O'Hare_International_Airport_UFO_sighting" }],
  'TU-2008-000001': [{ label: 'Stephenville UFO sightings (Wikipedia)', url: 'https://en.wikipedia.org/wiki/Stephenville_UFO_sightings' }],
};

/**
 * The famous-cases list: the built-in cases above plus historic cases added from the
 * admin (which carry historic_* fields on their report summary), sorted by year.
 * Used by /famous-ufo-cases on both the SSR page and the client screen.
 */
export function famousCaseList(
  reports: Array<{
    case_number: string;
    occurred_at: string;
    location_name?: string | null;
    historic_title?: string;
    historic_teaser?: string;
    historic_short_location?: string;
    historic_year?: number;
  }> = [],
): Array<[string, LegacyCaseMeta]> {
  const list = Object.entries(LEGACY_METADATA).map(([caseNumber, meta]) => [caseNumber, meta] as [string, LegacyCaseMeta]);
  const builtIn = new Set(Object.keys(LEGACY_METADATA));
  for (const r of reports) {
    const normalized = r.case_number.replace(/^SS-/, 'TU-').toUpperCase();
    if (!r.historic_title || builtIn.has(normalized)) continue;
    list.push([
      r.case_number,
      {
        title: r.historic_title,
        shortLocation: r.historic_short_location ?? (r.location_name ? String(r.location_name).split(',').slice(0, 2).join(',').trim() : ''),
        year: r.historic_year ?? new Date(r.occurred_at).getUTCFullYear(),
        teaser: r.historic_teaser ?? '',
      },
    ]);
  }
  return list.sort(([, a], [, b]) => a.year - b.year);
}
